import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Estimated Profile weights in kg/m for Klil 9000 (Heavy Duty System)
const PROFILE_WEIGHTS = {
    '03443': 1.150, '06175': 1.250, '03666': 0.850, '03449': 1.050,
    '03446': 1.450, '03448': 1.550, '03447': 0.950,
    '03437': 0.980, '03996': 0.880, '03465': 1.020, '03997': 0.920,
    '03439': 1.100, '03998': 1.000, '03358': 0.149,
    '04188': 1.200, '03450': 1.150, '04260': 0.900, '03966': 1.050,
    // Door specific profiles
    '03522': 1.150, '03524': 1.450, '06180': 1.650
};

const ProfileDisplay = ({ profileStr }) => {
    if (profileStr === '-') return <span>-</span>;
    const profiles = profileStr.split(' / ').map(p => p.trim());

    return (
        <div className="flex gap-3 items-center justify-start">
            {profiles.map(p => (
                <div key={p} className="flex items-center gap-2 bg-white border rounded p-1 shadow-sm print:border-none print:shadow-none">
                    <img
                        src={`/profiles/${p}.jpg`}
                        alt={`פרופיל ${p}`}
                        className="w-10 h-10 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="font-bold text-gray-800 px-1">{p}</span>
                </div>
            ))}
        </div>
    );
};

export default function Window9000Calculator() {
    const [type, setType] = useState('window'); // 'window' or 'door'
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [wings, setWings] = useState(2);
    const [glassType, setGlassType] = useState('14-24');

    // Advanced Inputs
    const [shutter, setShutter] = useState(false);
    const [fixedPart, setFixedPart] = useState('none');
    const [fixedHeight, setFixedHeight] = useState('');
    const [widePanel, setWidePanel] = useState(true); // Default to true for doors
    const [printMode, setPrintMode] = useState('detail');

    const L = parseFloat(width);
    const H = parseFloat(height);
    const H2 = parseFloat(fixedHeight) || 0;

    // H1 is the height of the sliding window/door part
    const H1 = fixedPart !== 'none' ? H - H2 : H;

    const getCalculatedData = () => {
        if (!L || !H || (fixedPart !== 'none' && !H2)) return null;

        // Klil 9000 Profile Selection based on Glass
        const pSashTB = glassType === '14-24' ? '03437' : '03996';
        const pSashSide = glassType === '14-24' ? '03465' : '03997';
        const pInterlock = glassType === '14-24' ? '03439' : '03998';

        let frame = [];
        let fixedProfiles = [];
        let sash = [];
        let glassParts = [];
        const trims = [
            { desc: 'הלבשה פנימית - רוחב', profile: '03358', qty: 2, cut: L + 50 },
            { desc: 'הלבשה פנימית - גובה', profile: '03358', qty: 2, cut: H + 50 },
        ];

        if (wings === 2) {
            let topTrack = shutter ? '04188' : '03443';
            // UPDATED: Changed 03522 to 03524 for the door floor track
            let bottomTrack = type === 'door' ? '03524' : '06175';
            let trackDesc = type === 'door' ? 'מסילה תחתונה (רצפתית לדלת)' : 'מסילה תחתונה';
            let jamb = shutter ? '03666' : '03666';
            let trackCut = L - 26;

            let sashWCut = (L / 2) - 12;
            let sashHCut = type === 'door' ? H1 - 39.5 : H1 - 80.5;

            // Adjustment for thinner profiles
            if (glassType !== '14-24') {
                sashHCut += 10.7;
            }

            if (fixedPart === 'bottom' || fixedPart === 'top') {
                frame.push({ desc: 'מסילה עליונה', profile: topTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'חציץ מעבר לקבוע', profile: '03966', qty: 1, cut: L - 26 });
                frame.push({ desc: trackDesc, profile: bottomTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'מזוזה כולל קבוע', profile: '03449', qty: 2, cut: H });

                fixedProfiles.push({ desc: 'קבוע צד', profile: '03450', qty: 2, cut: H2 - 17 });
                sashHCut = type === 'door' ? H1 - 47.8 : H1 - 88.8; // Adjusted for transom
            }
            else {
                frame.push({ desc: 'מסילה עליונה', profile: topTrack, qty: 1, cut: trackCut });
                frame.push({ desc: trackDesc, profile: bottomTrack, qty: 1, cut: trackCut });
                frame.push({ desc: 'מזוזה (צדדים)', profile: jamb, qty: 2, cut: H });
            }

            // If it's a door and wide panel is selected, swap the bottom sash profile
            if (type === 'door' && widePanel) {
                sash.push({ desc: 'כנף עליון', profile: pSashTB, qty: 2, cut: sashWCut });
                sash.push({ desc: 'פנל תחתון רחב', profile: '06180', qty: 2, cut: sashWCut });
            } else {
                sash.push({ desc: 'כנף עליון/תחתון', profile: pSashTB, qty: 4, cut: sashWCut });
            }

            sash.push({ desc: 'כנף צד', profile: pSashSide, qty: 2, cut: sashHCut });
            sash.push({ desc: 'שולב', profile: pInterlock, qty: 2, cut: sashHCut });

            // Glass height calculation
            let glassH = (type === 'door' && widePanel) ? sashHCut - 106 : sashHCut - 12;
            glassParts.push({ desc: `זכוכית כנף`, qty: 2, text: `${(sashWCut - 66).toFixed(1)} x ${glassH.toFixed(1)}` });

            if (fixedPart !== 'none') {
                glassParts.push({ desc: `זכוכית קבוע`, qty: 2, text: `${((L / 2) - 65.5).toFixed(1)} x ${(H2 - 60).toFixed(1)}` });
            }
        }
        else if (wings === 3) {
            let bottomTrack = type === 'door' ? '03524' : '03448';
            let trackDesc = type === 'door' ? 'מסילה תחתונה לדלת (3 נתיבים)' : 'מסילה תחתונה (3 נתיבים)';

            frame = [
                { desc: 'מסילה עליונה (3 נתיבים)', profile: '03446', qty: 1, cut: L - 27 },
                { desc: trackDesc, profile: bottomTrack, qty: 1, cut: L - 27 },
                { desc: 'מזוזה (3 נתיבים)', profile: '03447', qty: 2, cut: H }
            ];

            let sashWCut = (L / 3) + 5.4;
            let sashHCut = type === 'door' ? H1 - 39.5 : H1 - 85.5;

            if (glassType !== '14-24') sashHCut += 10.7;

            if (type === 'door' && widePanel) {
                sash.push({ desc: 'כנף עליון', profile: pSashTB, qty: 3, cut: sashWCut });
                sash.push({ desc: 'פנל תחתון רחב', profile: '06180', qty: 3, cut: sashWCut });
            } else {
                sash.push({ desc: 'כנף עליון/תחתון', profile: pSashTB, qty: 6, cut: sashWCut });
            }

            sash.push({ desc: 'כנף צד', profile: pSashSide, qty: 2, cut: sashHCut });
            sash.push({ desc: 'שולב פנימי/חיצוני', profile: pInterlock, qty: 4, cut: sashHCut });

            let glassH = (type === 'door' && widePanel) ? sashHCut - 106 : sashHCut - 12;
            glassParts.push({ desc: `זכוכית כנף`, qty: 3, text: `${(sashWCut - 66).toFixed(1)} x ${glassH.toFixed(1)}` });
        }

        let weight = 0;
        [...frame, ...fixedProfiles, ...sash, ...trims].forEach(item => {
            const kgPerMeter = PROFILE_WEIGHTS[item.profile] || 1.0;
            weight += (item.cut / 1000) * item.qty * kgPerMeter;
        });

        return { frame, fixedProfiles, sash, trims, glassParts, weight: weight.toFixed(2) };
    };

    const data = getCalculatedData();

    return (
        <div className="max-w-5xl mx-auto p-6 bg-gray-50 rounded-xl shadow-lg mt-10 print:shadow-none print:bg-white print:mt-0 print:p-0" dir="rtl">

            <Link to="/professionals" className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 mb-4 transition-colors font-bold print:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                חזרה למחשבונים
            </Link>

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-blue-900 print:text-black">מחשבון ייצור - קליל 9000</h2>

                {data && (
                    <div className="flex gap-2 print:hidden">
                        <button
                            onClick={() => { setPrintMode('detail'); setTimeout(() => window.print(), 100); }}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold transition-colors shadow-md text-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            דוח מפורט
                        </button>
                        <button
                            onClick={() => { setPrintMode('compact'); setTimeout(() => window.print(), 100); }}
                            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-bold transition-colors shadow-md text-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            כרטיס ייצור
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 bg-white p-6 rounded-lg shadow-sm border items-end print:hidden">
                <div>
                    <label className="block text-blue-900 font-bold mb-2">סוג פתח:</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} className="border-2 border-blue-400 bg-blue-50 p-2 rounded-lg w-full outline-none font-bold">
                        <option value="window">חלון (מסילה רגילה)</option>
                        <option value="door">דלת (מסילה רצפתית)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">רוחב פתח כולל (L) מ"מ:</label>
                    <input type="number" value={width} onChange={(e) => setWidth(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" placeholder="1200" />
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">גובה פתח כולל (H) מ"מ:</label>
                    <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" placeholder="2100" />
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">עובי זכוכית:</label>
                    <select value={glassType} onChange={(e) => setGlassType(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value="14-24">14 עד 24 מ"מ (בידודית חזקה)</option>
                        <option value="6-8">6 עד 8 מ"מ (רגילה)</option>
                    </select>
                </div>

                <div>
                    <label className="block text-gray-700 font-bold mb-2">מספר כנפיים:</label>
                    <select value={wings} onChange={(e) => setWings(Number(e.target.value))} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value={2}>2 כנפיים</option>
                        <option value={3}>3 כנפיים (3 נתיבים)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">תריס גלילה?</label>
                    <select value={shutter} onChange={(e) => setShutter(e.target.value === 'true')} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value="false">ללא תריס</option>
                        <option value="true">כולל תריס (מונובלוק)</option>
                    </select>
                </div>
                <div>
                    <label className="block text-gray-700 font-bold mb-2">קבוע (ללא פתיחה):</label>
                    <select value={fixedPart} onChange={(e) => setFixedPart(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none cursor-pointer bg-white">
                        <option value="none">ללא קבוע</option>
                        <option value="top">קבוע עליון</option>
                        <option value="bottom">קבוע תחתון</option>
                    </select>
                </div>

                {type === 'door' ? (
                    <div className="flex items-center gap-2 mt-2 md:mt-0 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                        <input type="checkbox" id="widePanel" checked={widePanel} onChange={(e) => setWidePanel(e.target.checked)} className="w-5 h-5 cursor-pointer" />
                        <label htmlFor="widePanel" className="text-gray-800 font-bold cursor-pointer">כולל פנל תחתון רחב?</label>
                    </div>
                ) : <div />}

                {fixedPart !== 'none' && (
                    <div className="md:col-span-4 bg-blue-50 p-4 rounded-lg border border-blue-200 mt-2">
                        <label className="block text-blue-900 font-bold mb-2">גובה הקבוע (H2) במ"מ:</label>
                        <input type="number" value={fixedHeight} onChange={(e) => setFixedHeight(e.target.value)} className="border-2 border-blue-300 p-2 rounded-lg w-1/3 outline-none" placeholder="לדוגמה: 400" />
                        <p className="text-sm text-blue-700 mt-2">גובה הכנפיים (H1) יחושב אוטומטית: {H1 > 0 ? `${H1} מ"מ` : '-'}</p>
                    </div>
                )}
            </div>

            {data && (
                <>
                    {/* ============ DETAILED VIEW ============ */}
                    <div className={`bg-white rounded-lg shadow-sm border overflow-hidden print:border-none print:shadow-none ${printMode === 'compact' ? 'print:hidden' : ''}`}>
                        <div className="hidden print:block mb-4 pb-4" style={{ borderBottom: '3px solid #1e3a5f' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <img src="/assets/logo-circle.png" alt="Alum Nawatha" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                    <div>
                                        <h1 style={{ fontSize: '24pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0, lineHeight: 1.2 }}>אלום נואטחה בע"מ</h1>
                                        <p style={{ fontSize: '10pt', color: '#555', margin: '2px 0 0 0', letterSpacing: '2px' }}>ALUM NAWATHA LTD</p>
                                        <p style={{ fontSize: '8pt', color: '#888', margin: '2px 0 0 0' }}>מערכות אלומיניום מתקדמות | טל: 052-878-4031</p>
                                    </div>
                                </div>
                                <table style={{ fontSize: '9pt', borderCollapse: 'collapse', textAlign: 'right', direction: 'rtl' }}>
                                    <tbody>
                                        <tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>תאריך:</td><td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{new Date().toLocaleDateString('he-IL')}</td></tr>
                                        <tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>מידות פתח:</td><td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{L} × {H} מ"מ</td></tr>
                                        {fixedPart !== 'none' && (<tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>חלוקת גובה:</td><td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>קבוע: {H2} | כנף: {H1}</td></tr>)}
                                        <tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>מפרט:</td><td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{wings} כנפיים {shutter && '| מותאם לתריס'}</td></tr>
                                        <tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333' }}>זכוכית:</td><td style={{ padding: '3px 12px', color: '#555' }}>{glassType} מ"מ</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13pt', fontWeight: 'bold', color: '#1e3a5f' }}>הוראת ייצור — מערכת קליל 9000 כבדה ({type === 'door' ? 'דלת' : 'חלון'})</div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-right border-collapse">
                                <thead>
                                    <tr className="bg-blue-100 border-b-2 border-blue-200 text-blue-900 print:bg-gray-100 print:border-gray-400 print:text-black">
                                        <th className="p-3 text-lg border-l print:border-gray-300">תיאור</th>
                                        <th className="p-3 text-lg border-l print:border-gray-300">פרופיל</th>
                                        <th className="p-3 text-lg border-l print:border-gray-300">כמות</th>
                                        <th className="p-3 text-lg">מידת חיתוך (מ"מ)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">מסגרת (משקוף)</td></tr>
                                    {data.frame.map((row, i) => (<tr key={`fr-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300"><td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td><td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td></tr>))}
                                    {data.fixedProfiles.length > 0 && (<><tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">מסגרת לקבוע</td></tr>{data.fixedProfiles.map((row, i) => (<tr key={`f-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300"><td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td><td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td></tr>))}</>)}
                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">כנפיים</td></tr>
                                    {data.sash.map((row, i) => (<tr key={`s-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300"><td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td><td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td></tr>))}
                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">הלבשות</td></tr>
                                    {data.trims.map((row, i) => (<tr key={`t-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300"><td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr={row.profile} /></td><td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-red-600 font-bold print:text-black">{row.cut}</td></tr>))}
                                    <tr className="bg-gray-100 border-b print:bg-gray-50 print:border-gray-400"><td className="p-2 font-bold text-blue-800 text-lg print:text-black" colSpan="4">זיגוג ({glassType} מ"מ)</td></tr>
                                    {data.glassParts.map((row, i) => (<tr key={`g-${i}`} className="border-b hover:bg-gray-50 print:border-gray-300"><td className="p-3 border-l print:border-gray-300">{row.desc}</td><td className="p-3 border-l print:border-gray-300"><ProfileDisplay profileStr="-" /></td><td className="p-3 font-bold text-gray-600 border-l print:border-gray-300 print:text-black">{row.qty}</td><td className="p-3 font-mono text-xl text-blue-600 font-bold print:text-black">{row.text}</td></tr>))}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-gray-800 text-white p-4 flex justify-between items-center rounded-b-lg print:bg-gray-100 print:text-black print:border-t-2 print:border-gray-400 print:mt-4">
                            <h3 className="text-lg font-bold">סה"כ משקל אלומיניום מוערך:</h3>
                            <div className="text-2xl font-mono font-bold text-yellow-400 print:text-black">{data.weight} <span className="text-sm text-gray-300 print:text-black">ק"ג</span></div>
                        </div>
                    </div>

                    {/* ============ COMPACT VIEW (Production Card) ============ */}
                    <div className={`hidden ${printMode === 'compact' ? 'print:block' : ''}`}>
                        <div className="mb-4 pb-3" style={{ borderBottom: '3px solid #1e3a5f' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <img src="/assets/logo-circle.png" alt="Alum Nawatha" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                                    <div>
                                        <h1 style={{ fontSize: '16pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0 }}>אלום נואטחה בע"מ</h1>
                                        <p style={{ fontSize: '8pt', color: '#888', margin: 0 }}>ALUM NAWATHA LTD | טל: 052-878-4031</p>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'left', fontSize: '9pt', color: '#555' }}>
                                    <div><b>תאריך:</b> {new Date().toLocaleDateString('he-IL')}</div>
                                    <div><b>מפרט:</b> {type === 'door' ? 'דלת' : 'חלון'} | {wings} כנפיים {shutter ? '+ תריס' : ''}</div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12pt', fontWeight: 'bold', color: '#1e3a5f' }}>כרטיס ייצור — קליל 9000 כבדה</div>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', fontSize: '10pt' }}>
                            <thead>
                                <tr style={{ background: '#e8f0fe' }}>
                                    <th style={{ border: '2px solid #1e3a5f', padding: '8px', width: '33%', fontSize: '12pt', color: '#1e3a5f' }}>מידות פתח</th>
                                    <th style={{ border: '2px solid #1e3a5f', padding: '8px', width: '34%', fontSize: '12pt', color: '#1e3a5f' }}>מסגרת</th>
                                    <th style={{ border: '2px solid #1e3a5f', padding: '8px', width: '33%', fontSize: '12pt', color: '#1e3a5f' }}>כנפיים</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style={{ verticalAlign: 'top' }}>
                                    <td style={{ border: '2px solid #1e3a5f', padding: '10px' }}>
                                        <div style={{ fontSize: '13pt', fontWeight: 'bold', textAlign: 'center', marginBottom: '8px', color: '#c00' }}>{L} × {H}</div>
                                        <div style={{ fontSize: '9pt', color: '#555', textAlign: 'center' }}>רוחב × גובה (מ"מ)</div>
                                        {fixedPart !== 'none' && (
                                            <div style={{ marginTop: '6px', padding: '4px', background: '#f5f5f5', borderRadius: '4px', fontSize: '9pt', textAlign: 'center' }}>
                                                <b>קבוע {fixedPart === 'top' ? 'עליון' : 'תחתון'}:</b> {H2}<br /><b>כנף:</b> {H1}
                                            </div>
                                        )}
                                        <div style={{ marginTop: '6px', fontSize: '9pt', textAlign: 'center' }}><b>זכוכית:</b> {glassType} מ"מ</div>
                                        {data.glassParts.map((g, i) => (
                                            <div key={i} style={{ marginTop: '4px', padding: '4px', background: '#eef6ff', borderRadius: '4px', fontSize: '9pt', textAlign: 'center' }}><b>{g.desc}:</b> {g.text}</div>
                                        ))}
                                    </td>
                                    <td style={{ border: '2px solid #1e3a5f', padding: '10px' }}>
                                        {data.frame.map((row, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                            </div>
                                        ))}
                                        {data.fixedProfiles.length > 0 && (<>
                                            <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1e3a5f', marginTop: '6px', borderBottom: '1px solid #ccc', paddingBottom: '2px' }}>קבוע:</div>
                                            {data.fixedProfiles.map((row, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                    <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                    <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                                </div>
                                            ))}
                                        </>)}
                                        <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1e3a5f', marginTop: '6px', borderBottom: '1px solid #ccc', paddingBottom: '2px' }}>הלבשות:</div>
                                        {data.trims.map((row, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                            </div>
                                        ))}
                                    </td>
                                    <td style={{ border: '2px solid #1e3a5f', padding: '10px' }}>
                                        {data.sash.map((row, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #eee' }}>
                                                <span style={{ fontSize: '9pt' }}>{row.desc} <span style={{ color: '#888' }}>({row.profile})</span></span>
                                                <span style={{ fontWeight: 'bold', fontFamily: 'monospace', color: '#c00', fontSize: '11pt' }}>{row.qty}× {row.cut}</span>
                                            </div>
                                        ))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div style={{ marginTop: '10px', padding: '8px', background: '#f3f3f3', textAlign: 'center', fontWeight: 'bold', fontSize: '11pt', borderRadius: '4px', border: '1px solid #ddd' }}>
                            משקל אלומיניום מוערך: {data.weight} ק"ג
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}